import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const ROOM_CONFIG = {
    seeblick: {
        resource: 'besprechungsraum-seeblick-fzugqlwtkpc8k5vx',
        placeholderTitle: 'teamWERK-Erolzheim Besprechungsraum Seeblick + Raum Vorstadt',
        displayName: 'Seeblick',
    },
    waldblick: {
        resource: 'besprechungsraum-waldblick-vxoketwugabmmmvy',
        placeholderTitle: 'teamWERK-Erolzheim Besprechungsraum Waldblick',
        displayName: 'Waldblick',
    },
};

function loadScript(src) {
    return new Promise((resolve, reject) => {
        // Check if script already loaded
        const existing = document.querySelector(`script[src="${src}"]`);
        if (existing) {
            resolve();
            return;
        }
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
    });
}

const AnnyBookingPage = () => {
    const { roomSlug } = useParams();
    const navigate = useNavigate();
    const widgetRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const config = ROOM_CONFIG[roomSlug];

    useEffect(() => {
        if (!config) {
            setError('Raum nicht gefunden');
            setLoading(false);
            return;
        }

        let mounted = true;

        const initWidget = async () => {
            try {
                // Load Vue 3 first, then anny components
                await loadScript('/assets/vendor/vue-3.5.43.global.prod.js'); // lokal statt unpkg (DSGVO)
                await loadScript('https://cdn.anny.co/widget/annyComponents.umd.latest.min.js');

                if (!mounted) return;

                // Small delay to let custom elements register
                setTimeout(() => {
                    if (mounted && widgetRef.current) {
                        // Clear and insert the widget element
                        widgetRef.current.innerHTML = '';
                        const el = document.createElement('a-resource-page');
                        el.setAttribute('base-url', 'https://anny.co');
                        el.setAttribute('resource', config.resource);
                        el.setAttribute('placeholder-title', config.placeholderTitle);
                        el.setAttribute('hide-header', 'false');
                        el.setAttribute('entity-id', '');
                        widgetRef.current.appendChild(el);
                        setLoading(false);
                    }
                }, 500);
            } catch (err) {
                console.error('Failed to load anny.co widget scripts:', err);
                if (mounted) {
                    setError('Das Buchungswidget konnte nicht geladen werden. Bitte versuchen Sie es später erneut.');
                    setLoading(false);
                }
            }
        };

        initWidget();

        return () => {
            mounted = false;
        };
    }, [config, roomSlug]);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <Navbar />
            <div className="pt-24 flex-1">
                {/* Header */}
                <div className="bg-gray-50 border-b">
                    <div className="container mx-auto px-4 py-6">
                        <div className="flex items-center gap-4">
                            <Button
                                variant="outline"
                                size="sm"
                                className="border-black text-black hover:bg-black hover:text-white rounded-none"
                                onClick={() => navigate('/')}
                            >
                                <ArrowLeft className="mr-2 h-4 w-4" />
                                Zurück
                            </Button>
                            {config && (
                                <div>
                                    <h1 className="text-2xl md:text-3xl font-bold font-heading">
                                        {config.displayName} buchen
                                    </h1>
                                    <p className="text-gray-500 text-sm mt-1">
                                        Online-Buchung über anny.co
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Widget Container */}
                <div className="container mx-auto px-4 py-8">
                    {error && (
                        <div className="text-center py-16">
                            <p className="text-red-600 text-lg mb-4">{error}</p>
                            <Button
                                className="bg-black hover:bg-gray-800 text-white rounded-none"
                                onClick={() => navigate('/')}
                            >
                                Zurück zur Startseite
                            </Button>
                        </div>
                    )}

                    {loading && !error && (
                        <div className="flex flex-col items-center justify-center py-16">
                            <Loader2 className="h-8 w-8 animate-spin text-black mb-4" />
                            <p className="text-gray-500">Buchungswidget wird geladen...</p>
                        </div>
                    )}

                    {/* The anny.co widget will be inserted here */}
                    <div
                        ref={widgetRef}
                        className="min-h-[600px] anny-widget-container"
                        style={{ display: loading || error ? 'none' : 'block' }}
                    />
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default AnnyBookingPage;
